<script setup lang="ts">
import VueMarkdown from 'vue-markdown-render';

const props = defineProps<{
  link: string;
  features: {
    title: string;
    cta: string;
    list: Record<
      string,
      {
        title: string;
        description: string;
        image: string;
        alt: string;
      }
    >;
  };
  themeKeys: {
    bg: string;
    title: string;
    featureTitle: string;
    text: string;
    ctaBg: string;
    ctaFg: string;
    ctaIcon: string;
  };
}>();

// animation
const refs = useTemplateRefsList();
const addWatchers = () => {
  for (const ref of [...refs.value]) {
    const visible = useElementVisibility(ref);
    watch(visible, (isVisible) => {
      if (isVisible) {
        // WARNING: If html structure changes, this too must change
        ref.childNodes[0].childNodes[0].classList.add(
          '!translate-x-0',
          'opacity-100',
        );
      }
    });
  }
};

const lang = useLocalStorage('CONTENT_LANG', 'en');
watch(lang, async () => {
  await nextTick();
  addWatchers();
});
onMounted(addWatchers);
onActivated(addWatchers);
</script>

<template>
  <div
    class="px-4 py-4 md:px-8 md:py-8 xl:px-32 xl:py-24"
    :style="{
      backgroundColor: props.themeKeys.bg,
    }"
  >
    <div class="inner-container">
      <h3
        class="font-semibold text-3xl w-full text-center mt-4 xl:-mt-6 mb-16"
        :style="{
          color: props.themeKeys.title,
        }"
      >
        {{ props.features.title }}
      </h3>
      <div
        class="
          flex flex-col gap-12 mb-8 sm:mb-16
          *:flex *:no-wrap *:justify-between *:gap-16 lg:*:gap-32
          *:odd:flex-col *:even:flex-col
          sm:*:odd:flex-row sm:*:even:flex-row-reverse
        "
      >
        <div
          class="group"
          v-for="feature in Object.values(props.features.list)"
          :key="feature.title"
          :ref="refs.set"
        >
          <div class="sm:w-1/2">
            <NuxtImg
              :src="feature.image"
              :alt="feature.alt"
              class="
                w-full h-full max-h-96 object-contain rounded-xl opacity-20
                group-odd:translate-x-[-20%] group-even:translate-x-[20%]
                transition-all delay-50 duration-1000 ease-in-out
              "
            />
          </div>
          <div class="sm:w-1/2">
            <h4
              class="my-4 font-semibold text-2xl max-w-[28rem]"
              :style="{
                color: props.themeKeys.featureTitle,
              }"
            >
              {{ feature.title }}
            </h4>
            <p
              :style="{
                color: props.themeKeys.text,
              }"
            >
              <VueMarkdown :source="feature.description" :options="{ html: true }" />
            </p>
          </div>
        </div>
      </div>
      <div class="mx-auto w-fit">
        <a
          :href="props.link"
          class="font-semibold p-3 rounded-2xl flex flex-row items-center hover:shadow-xl cursor-pointer transition"
          :style="{
            backgroundColor: props.themeKeys.ctaBg,
            color: props.themeKeys.ctaFg,
          }"
        >
          <Icon
            class="size-5 mr-2"
            :style="{ color: props.themeKeys.ctaIcon }"
            name="i-lucide-arrow-right"
          />
          {{ props.features.cta }}
        </a>
      </div>
    </div>
  </div>
</template>
